import {getTestProps} from '@benchling/js-test-utils';
import React from 'react';

import {BlockDomainVisualization, ChainDomainSizing, SharedDomainParams} from 'antibodies/AntibodyTypedefs';
import {AntibodyFormatDomainTerminusStyle} from 'graphql-client/__generated/graphqlGlobalTypes';

import AntibodyComponentPopover from './AntibodyComponentPopover';

export type AntibodyDomainProps = SharedDomainParams & {
  /* What color the fill (i.e. background) of the domain should be */
  fillColor: string;
  /* What color the stroke (i.e. border) of the domain should be */
  strokeColor: string;
  domainVisualization: BlockDomainVisualization;
  chainDomainSizing: ChainDomainSizing;
};

/**
 * A colored rectangle with rounded corners and one of a small number of short-end styles.
 * This is an SVG component, so all units are 'abstract user units', relative to the SVG viewbox.
 * @param domainVisualization The visualization for the domain
 * @param fillColor The fill color of the rectangle
 * @param strokeColor The stroke color of the rectangle
 * @param chainDomainSizing The sizing params for this format's chain domains
 */
export default function AntibodyDomain({
  domainVisualization,
  fillColor,
  strokeColor,
  chainDomainSizing,
  proteinId,
  inaccessible,
  label,
  showPopovers,
}: AntibodyDomainProps): JSX.Element {
  const {nTerminusStyle, cTerminusStyle} = domainVisualization;
  const {cornerRadius, height, width} = chainDomainSizing;
  // Draw a rectangle with rounded corners, with the N-terminus on the top and the C-terminus on the bottom.
  // Because we can have multiple TerminusStyle on the short ends, we do this with a `path`. Draw lines or
  // arcs (or a complex shape comprised of multiple lines and/or arcs) between eight points: the endpoints of
  // each side of the rectangle, which are knocked back slightly in order to create the rounded corners.
  const nTerminusLeft = {x: cornerRadius, y: 0};
  const nTerminusRight = {x: width - cornerRadius, y: 0};
  const rightTop = {x: width, y: cornerRadius};
  const rightBottom = {x: width, y: height - cornerRadius};
  const cTerminusRight = {x: width - cornerRadius, y: height};
  const cTerminusLeft = {x: cornerRadius, y: height};
  const leftBottom = {x: 0, y: height - cornerRadius};
  const leftTop = {x: 0, y: cornerRadius};
  // Now draw lines between them. Start at the top left and draw clockwise.
  const start = `M ${nTerminusLeft.x} ${nTerminusLeft.y}`;
  let nTerminusD: string;
  if (nTerminusStyle === AntibodyFormatDomainTerminusStyle.FLAT) {
    nTerminusD = `L ${nTerminusRight.x} ${nTerminusRight.y}`;
  } else {
    nTerminusD = `L ${width / 2} ${height / 8} L ${nTerminusRight.x} ${nTerminusRight.y}`;
  }
  const nToRightArc = `A ${cornerRadius} ${cornerRadius} 0 0 1 ${rightTop.x} ${rightTop.y}`;
  const rightD = `L ${rightBottom.x} ${rightBottom.y}`;
  const rightToCArc = `A ${cornerRadius} ${cornerRadius} 0 0 1 ${cTerminusRight.x} ${cTerminusRight.y}`;
  let cTerminusD: string;
  if (cTerminusStyle === AntibodyFormatDomainTerminusStyle.FLAT) {
    cTerminusD = `L ${cTerminusLeft.x} ${cTerminusLeft.y}`;
  } else {
    cTerminusD = `L ${width / 2} ${(height * 7) / 8} L ${cTerminusLeft.x} ${cTerminusLeft.y}`;
  }
  const cToLeftArc = `A ${cornerRadius} ${cornerRadius} 0 0 1 ${leftBottom.x} ${leftBottom.y}`;
  const leftD = `L ${leftTop.x} ${leftTop.y}`;
  const leftToNArc = `A ${cornerRadius} ${cornerRadius} 0 0 1 ${nTerminusLeft.x} ${nTerminusLeft.y}`;
  const paths = [
    start,
    nTerminusD,
    nToRightArc,
    rightD,
    rightToCArc,
    cTerminusD,
    cToLeftArc,
    leftD,
    leftToNArc,
  ].join(' ');
  const pathD = `${paths} Z`;
  return (
    <AntibodyComponentPopover
      proteinId={proteinId}
      inaccessible={inaccessible}
      componentLabel={label}
      showPopovers={showPopovers}
    >
      <path
        d={pathD}
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={strokeColor === 'transparent' ? 0 : width / 16}
        shapeRendering="geometricPrecision"
        {...getTestProps({component: 'AntibodyDomain', element: 'popoverToggle'})}
      />
    </AntibodyComponentPopover>
  );
}
